<template>
  <div class="service-price-audit-page">
    <!-- 顶部标题卡片 -->
    <div class="page-header">
      <div class="header-icon">
        <i class="el-icon-document-copy" />
      </div>
      <div class="header-content">
        <h1 class="page-title">选型报价工具审计</h1>
        <p class="page-subtitle">管理邀请码和审核工具使用记录</p>
      </div>
    </div>

    <!-- 主容器 -->
    <div class="audit-container">
      <div class="audit-layout" :style="{ userSelect: isDragging ? 'none' : 'auto' }">
        <!-- 左侧：邀请码管理 -->
        <div class="layout-left" :style="{ width: leftPanelWidth + 'px' }">
          <div class="section-header">
            <span class="section-title">
              <i class="el-icon-key" /> 邀请码列表
              <small style="margin-left: 8px; font-weight: normal; color: #909399;">
                (总计: {{ inviteCodeList.length }})
              </small>
            </span>
            <div class="button-group">
              <el-button type="primary" icon="el-icon-plus" size="mini" @click="showCreateDialog = true">
                生成邀请码
              </el-button>
              <el-button
                icon="el-icon-refresh"
                size="mini"
                @click="
                  () => {
                    inviteCodePager.page = 1
                    loadInviteCodeList()
                  }
                "
              >
                刷新
              </el-button>
            </div>
          </div>

          <div class="table-wrapper">
            <el-table
              v-loading="inviteCodeLoading"
              :data="inviteCodePageData"
              highlight-current-row
              style="width: 100%"
              size="mini"
              @current-change="handleInviteCodeSelect"
            >
              <el-table-column label="邀请码" min-width="150" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <span class="code-text-highlight">{{ row.inviteCode }}</span>
                </template>
              </el-table-column>
              <el-table-column
                label="创建人"
                prop="createUserName"
                min-width="90"
                show-overflow-tooltip
                align="center"
              />
              <el-table-column label="时效" min-width="60" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="getTagTypeByHours(row.expireHours, row.expired)" size="mini">
                    {{ row.expireHours }}h
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="剩余" min-width="60" align="center">
                <template slot-scope="{ row }">
                  <span :class="['remain-time', getTagTypeByHours(calcRemainHours(row), row.expired)]">
                    {{ displayRemainTime(row) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="创建时间" min-width="140" align="center">
                <template slot-scope="{ row }">
                  <span class="time-text">{{ formatDateTime(row.createTime) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" align="center">
                <template slot-scope="{ row }">
                  <div class="operation-buttons">
                    <el-tooltip :content="row._copied ? '已复制' : '复制邀请码'" placement="top" :key="row.id">
                      <el-button
                        type="text"
                        size="mini"
                        :class="{ 'copy-btn-icon': true, copied: row._copied }"
                        @click.stop="handleCopyCode(row)"
                      >
                        <span class="copy-icon-inline" aria-hidden="true">
                          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                            <path
                              d="M16 1H6C4.9 1 4 1.9 4 3v12h2V3h10V1zm3 4H10C8.9 5 8 5.9 8 7v14c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16h-9V7h9v14z"
                              fill="currentColor"
                            />
                          </svg>
                        </span>
                      </el-button>
                    </el-tooltip>
                    <el-button
                      type="text"
                      size="mini"
                      class="text-danger"
                      icon="el-icon-delete"
                      @click.stop="handleDeleteCode(row)"
                    />
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <!-- 左侧分页 -->
            <div class="pager-wrapper">
              <div class="pager-total">总计: {{ inviteCodeList.length }}</div>
              <el-pagination
                v-if="inviteCodeList.length > 0"
                small
                layout="prev, pager, next"
                :current-page.sync="inviteCodePager.page"
                :page-size="inviteCodePager.limit"
                :total="inviteCodeList.length"
              />
            </div>
          </div>
        </div>

        <!-- 可拖拉的分隔线 -->
        <div class="layout-divider" @mousedown="handleDividerMouseDown"></div>

        <!-- 右侧：审计记录 -->
        <div class="layout-right">
          <div class="section-header">
            <span class="section-title">
              <i class="el-icon-document" /> 审计记录
              <small style="margin-left: 8px; font-weight: normal; color: #909399;">
                (总计: {{ auditList.length }}{{ selectedCode ? ', 邀请码: ' + selectedCode.inviteCode : '' }})
              </small>
            </span>
            <el-button
              v-if="selectedCode"
              icon="el-icon-refresh"
              size="mini"
              @click="
                () => {
                  auditPager.page = 1
                  loadAuditList()
                }
              "
            >
              刷新
            </el-button>
          </div>

          <div class="table-wrapper">
            <el-table
              v-loading="auditLoading"
              :data="auditPageData"
              stripe
              style="width: 100%"
              size="mini"
              @row-click="handleAuditRowClick"
            >
              <el-table-column label="邀请码" min-width="180" show-overflow-tooltip>
                <template slot-scope="{ row }">
                  <span class="code-text">{{ row.inviteCode }}</span>
                </template>
              </el-table-column>
              <el-table-column label="使用人" width="120" prop="createUserName" align="center" show-overflow-tooltip />
              <el-table-column label="使用时间" width="160" align="center">
                <template slot-scope="{ row }">
                  {{ formatDateTime(row.createTime) }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="80" align="center">
                <template slot-scope="{ row }">
                  <el-button type="text" size="mini" @click.stop="handleAuditRowClick(row)">
                    详情
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div
              v-if="!selectedCode || (selectedCode && auditList.length === 0 && !auditLoading)"
              class="empty-state"
              style="padding: 100px 0;"
            >
              <i class="el-icon-document" />
              <p v-if="!selectedCode">请点击左侧邀请码查看对应的审计记录</p>
              <p v-else>该邀请码暂无使用记录</p>
            </div>
            <!-- 右侧分页 -->
            <div class="pager-wrapper">
              <div class="pager-total">总计: {{ auditList.length }}</div>
              <el-pagination
                v-if="auditList.length > 0"
                small
                layout="prev, pager, next"
                :current-page.sync="auditPager.page"
                :page-size="auditPager.limit"
                :total="auditList.length"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 生成邀请码弹窗 -->
    <el-dialog title="生成邀请码" :visible.sync="showCreateDialog" width="600px" @close="resetCreateForm">
      <div>
        <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="100px" size="small">
          <el-form-item label="有效时效" prop="expireHours" style="margin-bottom: 0;">
            <el-select v-model="createForm.expireHours" placeholder="请选择有效时效" style="width: 200px">
              <el-option label="8 小时" :value="8" />
              <el-option label="24 小时 (1天)" :value="24" />
              <el-option label="48 小时 (2天)" :value="48" />
              <el-option label="72 小时 (3天)" :value="72" />
              <el-option label="168 小时 (7天)" :value="168" />
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <!-- 生成后的显示区域 -->
      <div v-if="createdInviteCode" class="create-success-panel">
        <div class="success-tip">
          <i class="el-icon-success" />
          <span>邀请码生成成功</span>
        </div>
        <div class="result-box">
          <div class="result-label">最新生成的邀请码：</div>
          <div class="result-code-wrapper">
            <span class="result-code-text">{{ createdInviteCode }}</span>
            <el-button
              type="text"
              size="mini"
              :class="{ 'copy-btn': true, copied: createdInviteCodeCopied }"
              icon="el-icon-document-copy"
              @click="handleCopyCreatedCode"
            >
              {{ createdInviteCodeCopied ? '已复制' : '复制' }}
            </el-button>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="showCreateDialog = false">关闭</el-button>
        <el-button type="primary" size="small" :loading="createLoading" @click="handleCreateCode">
          生成
        </el-button>
      </div>
    </el-dialog>

    <!-- 详情抽屉 -->
    <el-drawer
      title="操作记录详情"
      :visible.sync="showDetailDrawer"
      direction="rtl"
      size="70%"
      class="detail-drawer"
      @close="() => (showDetailDrawer = false)"
    >
      <div v-if="detailLoading" class="drawer-loading">
        <i class="el-icon-loading" />
        <span>加载中...</span>
      </div>
      <div v-else-if="detailData" class="drawer-content">
        <div class="detail-meta">
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="meta-item">
                <span class="meta-label">操作人：</span>
                <span>{{ detailData.createUserName || '-' }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="meta-item">
                <span class="meta-label">操作时间：</span>
                <span>{{ formatDateTime(detailData.createTime) }}</span>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="24">
              <div class="meta-item">
                <span class="meta-label">邀请码：</span>
                <span class="code-text">{{ detailData.inviteCode }}</span>
              </div>
            </el-col>
          </el-row>
        </div>

        <div v-if="parsedContent" class="detail-sections">
          <!-- 明细清单 -->
          <div v-if="detailRows.length > 0" class="detail-section">
            <h4 class="section-title">明细清单</h4>
            <el-table :data="detailRows" size="mini" stripe border style="width: 100%; min-width: 1180px;">
              <el-table-column
                v-for="col in detailColumns"
                :key="col.prop"
                :label="col.label"
                :prop="col.prop"
                :width="col.width"
                :min-width="col.minWidth"
                :align="col.align"
                show-overflow-tooltip
              >
                <template slot-scope="{ row }">
                  <span v-if="col.prop === 'name'" class="detail-name">{{ row.name || '-' }}</span>
                  <span v-else-if="col.prop === 'quantity'">{{ formatQuantity(row.quantity) }}</span>
                  <span v-else-if="col.prop === 'discount'">{{ formatPercentValue(row.discount) }}</span>
                  <span
                    v-else-if="col.prop === 'profitRate'"
                    :class="['profit-rate-text', getProfitRateClass(row.profitRate)]"
                  >
                    {{ formatPercentRate(row.profitRate) }}
                  </span>
                  <span v-else :class="getMoneyClass(row[col.prop])">{{ formatMoneyCell(row[col.prop]) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 汇总信息 -->
          <div v-if="detailSummary" class="detail-section">
            <h4 class="section-title">汇总数据</h4>
            <div class="summary-grid">
              <div v-for="(item, idx) in summaryItems" :key="idx" :class="['summary-card', item.cardClass]">
                <div class="summary-label">{{ item.label }}</div>
                <div class="summary-value">{{ item.value }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="raw-content">
          <h4 class="section-title">原始数据</h4>
          <pre class="json-pre">{{ detailData.content }}</pre>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { Button, Table, TableColumn, Drawer, Dialog, Form, Select, Option, Pagination, Tag, Message } from 'element-ui'

import { listInviteCode, createInviteCode, deleteInviteCode, listOpAudit, detailOpAudit } from '@/api/servicePrice'

export default {
  name: 'ServicePriceAudit',
  components: {
    [Button.name]: Button,
    [Select.name]: Select,
    [Option.name]: Option,
    [Form.name]: Form,
    [Dialog.name]: Dialog,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Drawer.name]: Drawer,
    [Pagination.name]: Pagination,
    [Tag.name]: Tag
  },
  data() {
    return {
      // 标签页
      activeTab: 'inviteCodes',

      // 左侧邀请码
      inviteCodeLoading: false,
      inviteCodeList: [],
      selectedCode: null,
      inviteCodePager: {
        page: 1,
        limit: 20
      },

      // 右侧审计记录
      auditLoading: false,
      auditList: [],
      auditPager: {
        page: 1,
        limit: 20
      },

      // 详情抽屉
      showDetailDrawer: false,
      detailLoading: false,
      detailData: null,
      parsedContent: null,

      // 生成邀请码弹窗
      showCreateDialog: false,
      createLoading: false,
      createForm: {
        expireHours: 24
      },
      createRules: {
        expireHours: [{ required: true, message: '请选择有效时效', trigger: 'change' }]
      },

      // 详情表格列配置
      detailColumns: [
        { label: '项目名称', prop: 'name', minWidth: 220 },
        { label: '数量', prop: 'quantity', width: 90, align: 'center' },
        { label: 'list单价', prop: 'listUnitPrice', width: 120, align: 'right' },
        { label: 'list总价', prop: 'listTotalPrice', width: 130, align: 'right' },
        { label: '折扣(%)', prop: 'discount', width: 90, align: 'center' },
        { label: '出货总价', prop: 'finalTotalPrice', width: 130, align: 'right' },
        { label: '成本单价', prop: 'costUnitPrice', width: 120, align: 'right' },
        { label: '成本总价', prop: 'costTotalPrice', width: 130, align: 'right' },
        { label: '毛利额', prop: 'profit', width: 130, align: 'right' },
        { label: '毛利率', prop: 'profitRate', width: 110, align: 'center' }
      ],

      // 分隔线拖拉
      isDragging: false,
      leftPanelWidth: 650,

      // 生成结果
      createdInviteCode: null,
      createdInviteCodeCopied: false
    }
  },

  computed: {
    inviteCodePageData() {
      const start = (this.inviteCodePager.page - 1) * this.inviteCodePager.limit
      const end = start + this.inviteCodePager.limit
      return this.inviteCodeList.slice(start, end)
    },
    auditPageData() {
      const start = (this.auditPager.page - 1) * this.auditPager.limit
      const end = start + this.auditPager.limit
      return this.auditList.slice(start, end)
    },
    detailRows() {
      return this.getDetailRows(this.parsedContent)
    },
    detailSummary() {
      return this.getDetailSummary(this.parsedContent)
    },
    summaryItems() {
      if (!this.detailSummary) return []
      const summary = this.detailSummary
      return [
        { label: '总成本', value: this.formatMoney(summary.totalCost), cardClass: 'summary-card-cost' },
        { label: '出货总价', value: this.formatMoney(summary.finalTotal), cardClass: 'summary-card-sales' },
        { label: '总毛利额', value: this.formatMoney(summary.totalProfit), cardClass: 'summary-card-profit' },
        {
          label: '毛利率',
          value: summary.profitRate != null ? (summary.profitRate * 100).toFixed(2) + '%' : '-',
          cardClass: 'summary-card-rate'
        }
      ]
    }
  },

  mounted() {
    this.loadInviteCodeList()
    document.addEventListener('mousemove', this.handleDividerMouseMove)
    document.addEventListener('mouseup', this.handleDividerMouseUp)
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleDividerMouseMove)
    document.removeEventListener('mouseup', this.handleDividerMouseUp)
  },

  methods: {
    showSuccess(message) {
      if (this.$message && this.$message.success) {
        this.$message.success(message)
        return
      }
      Message.success(message)
    },

    showError(message) {
      if (this.$message && this.$message.error) {
        this.$message.error(message)
        return
      }
      Message.error(message)
    },

    showWarning(message) {
      if (this.$message && this.$message.warning) {
        this.$message.warning(message)
        return
      }
      Message.warning(message)
    },

    // ========== 邀请码管理 ==========
    loadInviteCodeList(silent = false) {
      this.inviteCodeLoading = !silent
      listInviteCode({})
        .then(res => {
          const responseData = res && res.data ? res.data : res || {}
          if (responseData.content) {
            this.inviteCodeList = responseData.content.itemArr || []
          }
        })
        .catch(err => {
          this.showError('加载邀请码失败：' + (err.message || err))
        })
        .finally(() => {
          if (!silent) {
            this.inviteCodeLoading = false
          }
        })
    },

    handleInviteCodeSelect(row) {
      this.selectedCode = row
      if (row) {
        this.loadAuditList()
      }
    },

    handleCopyCode(row) {
      const el = document.createElement('textarea')
      el.value = row.inviteCode
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      // 设置复制状态
      this.$set(row, '_copied', true)
      this.showSuccess('已复制到剪贴板')
      // 3秒后恢复状态
      setTimeout(() => {
        this.$set(row, '_copied', false)
      }, 3000)
    },

    handleCreateCode() {
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        this.createLoading = true
        // 重置生成状态
        this.createdInviteCode = null
        this.createdInviteCodeCopied = false

        createInviteCode({ expireHours: this.createForm.expireHours })
          .then(res => {
            const responseData = res && res.data ? res.data : res || {}
            if (responseData.status === 'SUCCESS') {
              // 处理返回结构
              const content = responseData.content || responseData || {}
              if (content.inviteCode) {
                this.createdInviteCode = content.inviteCode
                // 自动刷新左侧列表，先本地插入再远程拉取，保证立刻可见
                const createdRow = { ...content, _copied: false }
                this.inviteCodePager.page = 1
                this.inviteCodeList = [
                  createdRow,
                  ...this.inviteCodeList.filter(item => item.inviteCode !== createdRow.inviteCode)
                ]
                this.loadInviteCodeList(true)
                this.showSuccess('邀请码生成成功')
              } else {
                this.showCreateDialog = false
                this.inviteCodePager.page = 1
                this.loadInviteCodeList(true)
                this.showSuccess('生成成功')
              }
            } else {
              const errorMsg = responseData.msg || '生成失败，请重试'
              this.showError(errorMsg)
            }
          })
          .catch(err => {
            const errorMsg = err.data?.msg || err.message || '生成失败，请重试'
            this.showError(errorMsg)
          })
          .finally(() => {
            this.createLoading = false
          })
      })
    },

    handleCopyCreatedCode() {
      if (!this.createdInviteCode) return
      const el = document.createElement('textarea')
      el.value = this.createdInviteCode
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)

      this.createdInviteCodeCopied = true
      this.showSuccess('已复制到剪贴板')
      setTimeout(() => {
        this.createdInviteCodeCopied = false
      }, 3000)
    },

    resetCreateForm() {
      if (this.$refs.createForm) {
        this.$refs.createForm.resetFields()
      }
      this.createdInviteCode = null
      this.createdInviteCodeCopied = false
    },

    handleDeleteCode(row) {
      this.$confirm('删除后受邀用户将无法使用该邀请码，确定要删除吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          deleteInviteCode({ id: row.id })
            .then(res => {
              const responseData = res && res.data ? res.data : res || {}
              if (responseData.success !== false) {
                this.showSuccess('删除成功')
                this.loadInviteCodeList()
              } else {
                this.showError(responseData.msg || '删除失败')
              }
            })
            .catch(err => {
              this.showError('删除失败：' + (err.message || err))
            })
        })
        .catch(() => {
          // 取消删除
        })
    },

    // ========== 审计记录 ==========
    loadAuditList() {
      if (!this.selectedCode || !this.selectedCode.inviteCode) {
        this.showWarning('请先选择邀请码')
        return
      }
      this.auditLoading = true
      listOpAudit({
        matchArr: [
          {
            type: 'EXACT',
            fieldName: 'inviteCode',
            valueArr: [this.selectedCode.inviteCode],
            matchRule: 'EQ'
          }
        ]
      })
        .then(res => {
          const responseData = res && res.data ? res.data : res || {}
          if (responseData.content && responseData.content.itemArr) {
            this.auditList = responseData.content.itemArr
          } else {
            this.auditList = []
          }
        })
        .catch(err => {
          const errorMsg = err.data?.msg || err.message || '加载审计记录失败，请重试'
          this.showError(errorMsg)
          this.auditList = []
        })
        .finally(() => {
          this.auditLoading = false
        })
    },

    handleAuditRowClick(row) {
      this.showDetailDrawer = true
      this.detailData = null
      this.parsedContent = null
      this.detailLoading = true
      detailOpAudit({ id: row.id })
        .then(res => {
          const responseData = res && res.data ? res.data : res || {}
          if (responseData.content !== undefined) {
            this.detailData = responseData.content
            this.parsedContent = this.normalizeDetailContent(responseData.content)
          } else {
            this.detailData = responseData
            this.parsedContent = this.normalizeDetailContent(responseData)
          }
        })
        .catch(err => {
          this.showError('加载详情失败：' + (err.message || err))
        })
        .finally(() => {
          this.detailLoading = false
        })
    },

    getAuditSummary(row) {
      if (!row.content) return '—'
      try {
        const data = JSON.parse(row.content)
        // 新格式：包含 snapshot 和 priceData 的结构
        if (data.priceData && data.priceData.summary) {
          const summary = data.priceData.summary
          return `出货总价: ${this.formatMoney(summary.finalTotal)} | 毛利率: ${
            summary.profitRate != null ? (summary.profitRate * 100).toFixed(1) + '%' : '-'
          }`
        }
        // 兼容旧格式
        if (data.summary) {
          return `出货总价: ${this.formatMoney(data.summary.finalTotal)} | 毛利率: ${
            data.summary.profitRate != null ? (data.summary.profitRate * 100).toFixed(1) + '%' : '-'
          }`
        }
        // 兼容旧格式：直接的 rows
        if (data.rows) {
          return `共 ${data.rows.length} 行明细数据`
        }
        // 兼容旧格式：priceData.rows（没有外层 snapshot 的情况）
        if (data.priceData && data.priceData.rows) {
          return `共 ${data.priceData.rows.length} 行明细数据`
        }
      } catch (e) {
        // ignore
      }
      return '明细数据'
    },

    normalizeDetailContent(content) {
      if (content == null) return null

      if (typeof content === 'string') {
        try {
          return this.normalizeDetailContent(JSON.parse(content))
        } catch (e) {
          return { rawContent: content }
        }
      }

      if (Array.isArray(content)) {
        return { rows: content }
      }

      if (typeof content === 'object') {
        // 新格式：包含 snapshot 和 priceData 的结构（从 buildAuditSnapshot 生成）
        if (content.snapshot && content.priceData) {
          return {
            ...content.priceData,
            snapshot: content.snapshot,
            recordedAt: content.recordedAt
          }
        }

        if (Array.isArray(content.rows) || content.summary) return content
        if (Array.isArray(content.itemArr)) {
          return { ...content, rows: content.itemArr }
        }
        if (Array.isArray(content.list)) {
          return { ...content, rows: content.list }
        }
        if (Array.isArray(content.dataList)) {
          return { ...content, rows: content.dataList }
        }
        if (content.content !== undefined) {
          return this.normalizeDetailContent(content.content)
        }
        return content
      }

      return null
    },

    getDetailRows(content) {
      if (!content) return []
      // 新格式支持：priceData.rows
      if (content.priceData && Array.isArray(content.priceData.rows)) {
        return content.priceData.rows
      }
      // 兼容旧格式
      if (Array.isArray(content.rows)) return content.rows
      if (Array.isArray(content.itemArr)) return content.itemArr
      if (Array.isArray(content.list)) return content.list
      if (Array.isArray(content.dataList)) return content.dataList
      return []
    },

    getDetailSummary(content) {
      if (!content) return null
      // 新格式支持：priceData.summary
      if (content.priceData && content.priceData.summary) {
        return content.priceData.summary
      }
      // 兼容旧格式
      return content.summary || null
    },

    // ========== 工具方法 ==========
    shortCode(code) {
      if (!code) return '-'
      if (code.length > 22) {
        return code.substring(0, 12) + '...' + code.substring(code.length - 6)
      }
      return code
    },

    formatDateTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      return date.toLocaleString('zh-CN')
    },

    formatMoney(val) {
      if (val == null) return '¥ 0.00'
      return '¥ ' + parseFloat(val).toFixed(2)
    },

    formatMoneyCell(val) {
      if (val == null || val === '') return '-'
      const num = Number(val)
      if (Number.isNaN(num)) return val
      return '¥ ' + num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    },

    formatQuantity(val) {
      if (val == null || val === '') return '-'
      return String(val)
    },

    formatPercentValue(val) {
      if (val == null || val === '') return '-'
      return String(val)
    },

    formatPercentRate(val) {
      if (val == null || val === '') return '-'
      if (typeof val === 'number') {
        return (val * 100).toFixed(2) + '%'
      }
      return String(val)
    },

    getProfitRateClass(val) {
      const rate = typeof val === 'number' ? val : parseFloat(val)
      if (Number.isNaN(rate)) return ''
      if (rate < 0) return 'negative-rate'
      if (rate === 0) return 'zero-rate'
      return 'positive-rate'
    },

    getMoneyClass(val) {
      const num = Number(val)
      if (Number.isNaN(num)) return ''
      if (num < 0) return 'negative-money'
      if (num > 0) return 'positive-money'
      return 'zero-money'
    },

    calcRemainHours(info) {
      if (!info || !info.expireHours) return info?.expireHours || '-'
      if (!info.createTime) return info.expireHours
      const expireMs = new Date(info.createTime).getTime() + info.expireHours * 3600000
      const remainMs = expireMs - Date.now()
      if (remainMs <= 0) return 0
      return Math.max(1, Math.floor(remainMs / 3600000))
    },

    displayRemainTime(row) {
      if (row.expired) return '已过期'
      const remain = this.calcRemainHours(row)
      if (remain <= 0) return '已过期'
      return remain + 'h'
    },

    getTagTypeByHours(hours, expired) {
      if (expired) return 'info'
      if (hours <= 24) return 'danger'
      if (hours <= 48) return 'warning'
      if (hours <= 72) return 'primary'
      return 'success'
    },

    handleDividerMouseDown() {
      this.isDragging = true
    },

    handleDividerMouseMove(e) {
      if (!this.isDragging) return
      const container = document.querySelector('.audit-layout')
      if (!container) return
      const rect = container.getBoundingClientRect()
      const newWidth = e.clientX - rect.left
      if (newWidth > 300 && newWidth < rect.width - 300) {
        this.leftPanelWidth = newWidth
      }
    },

    handleDividerMouseUp() {
      this.isDragging = false
    }
  }
}
</script>

<style scoped>
.service-price-audit-page {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #f0f2f5;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 页面标题 */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #fff;
  flex-shrink: 0;
}

.header-content {
  flex: 1;
}

.page-title {
  margin: 0 0 4px 0;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.page-subtitle {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

/* 主容器 */
.audit-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.audit-layout {
  display: flex;
  height: 100%;
  width: 100%;
}

.layout-left {
  width: 500px;
  border-right: 1px solid #ebeef5;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: #fafafa;
  flex-shrink: 0;
}

.layout-divider {
  width: 4px;
  background: linear-gradient(90deg, #ebeef5 0%, #ddd 50%, #ebeef5 100%);
  cursor: col-resize;
  transition: background 0.3s;
  flex-shrink: 0;
}

.layout-divider:hover {
  background: linear-gradient(90deg, #667eea 0%, #667eea 50%, #667eea 100%);
}

.layout-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-width: 0;
}

.table-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.table-wrapper >>> .el-table {
  flex: 1;
  cursor: pointer;
}

.audit-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.audit-tabs >>> .el-tabs__content {
  flex: 1;
  overflow-y: auto;
}

.tab-content {
  padding: 20px;
}

/* 区域标题 */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #667eea;
  font-size: 18px;
}

/* 表格样式 */
.code-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.code-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
}

.audit-summary {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}

/* 展开行 */
.expand-detail {
  padding: 12px 0;
  background: #f8f9fc;
  border-radius: 4px;
  margin: 8px 0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: #909399;
}

.detail-value {
  font-size: 13px;
  color: #303133;
  font-weight: 500;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: #c0c4cc;
}

.empty-state i {
  font-size: 48px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* 抽屉 */
.drawer-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 200px;
  color: #909399;
}

.drawer-content {
  padding: 20px 8px 18px;
  overflow-y: auto;
  max-height: calc(100vh - 120px);
}

.detail-drawer >>> .el-drawer__header {
  margin-bottom: 0;
  padding: 18px 24px 14px;
}

.detail-drawer >>> .el-drawer__header span {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.detail-meta {
  background: #f8f9fc;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.meta-item {
  font-size: 13px;
  color: #606266;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.meta-label {
  color: #909399;
  font-weight: 500;
  min-width: 80px;
}

/* 详情区域 */
.detail-sections {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-section .section-title {
  font-size: 14px;
}

.detail-name {
  font-weight: 600;
  color: #303133;
}

.positive-money,
.positive-rate {
  color: #f56c6c;
  font-weight: 600;
}

.negative-money,
.negative-rate {
  color: #67c23a;
  font-weight: 600;
}

.zero-money,
.zero-rate {
  color: #909399;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-card {
  min-height: 72px;
  border-radius: 6px;
  padding: 12px 16px;
  color: #303133;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.summary-label {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 18px;
  font-weight: 700;
}

.summary-card-cost {
  background: #eef5ff;
}

.summary-card-sales {
  background: #fff6eb;
}

.summary-card-profit {
  background: #fff1f2;
}

.summary-card-rate {
  background: #faf1f4;
}

.json-pre {
  font-size: 11px;
  color: #606266;
  background: #f8f9fc;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.6;
  border: 1px solid #ebeef5;
  margin: 0;
}

.raw-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.text-danger {
  color: #f56c6c !important;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 邀请码行样式 */
.code-row-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
}

.code-row-header {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}

.code-text-highlight {
  font-weight: 600;
  color: #409eff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  flex: 1;
  word-break: break-all;
}

.code-row-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #909399;
}

.copy-btn {
  font-size: 12px;
  padding: 4px 8px;
  color: #409eff;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  color: #66b1ff;
}

.copy-btn {
  font-size: 12px;
  padding: 4px 8px;
  color: #409eff;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  color: #66b1ff;
}

.copy-btn.copied {
  color: #67c23a;
}

.copy-btn-icon {
  font-size: 16px;
  padding: 4px;
  color: #409eff;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.copy-btn-icon:hover {
  color: #66b1ff;
  transform: scale(1.1);
}

.copy-btn-icon.copied {
  color: #67c23a;
}

.copy-icon-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  line-height: 1;
}

.copy-icon-inline svg {
  width: 14px;
  height: 14px;
  display: block;
}

.remain-time {
  font-weight: 500;
  font-size: 12px;
}

.remain-time.danger {
  color: #f56c6c;
}

.remain-time.warning {
  color: #e6a23c;
}

.remain-time.success {
  color: #67c23a;
}

.remain-time.primary {
  color: #409eff;
}

.remain-time.info {
  color: #909399;
}

.button-group {
  display: flex;
  gap: 8px;
}

.code-row-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.code-text-highlight {
  font-weight: 600;
  color: #409eff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  word-break: break-all;
}

.code-row-meta {
  font-size: 12px;
  color: #909399;
}

.operation-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

/* 生成成功面板 */
.create-success-panel {
  padding: 0 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.success-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #67c23a;
  font-size: 14px;
  font-weight: 600;
}

.success-tip i {
  font-size: 20px;
}

.result-box {
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  border-radius: 4px;
  padding: 12px 16px;
}

.result-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
}

.result-code-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-code-text {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #409eff;
  word-break: break-all;
  flex: 1;
}

.pager-wrapper {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.pager-total {
  font-size: 12px;
  color: #909399;
}
</style>
